import { FC, Fragment, ReactNode, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "@/stores/RootStoreContext";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { MSelect } from "@/components-v2/Select";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { Alert } from "@/components-v2/Alert";
import { Dialog } from "@/components-v2/Dialog";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { MTextInput } from "@/components-v2/TextInput";
import { MTextArea } from "@/components-v2/TextArea";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { InfoButton } from "@/components-v2/InfoButton";
import { Icon } from "@/components-v2/Icon";
import { Sortable, SortableChildrenProps, SortableList } from "@/components-v2/SortableList";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import {
  LawReference,
  lawReferenceLabel,
  lawReferenceUrl,
  SELECTABLE_ARTICLE_TYPES,
} from "@/common/articleTypes";
import { languageLabel } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { IncorporationForm } from "./incorporationDomain";
import {
  ArticleSummary,
  IncorporationArticleDialogModel,
  IncorporationArticlesModel,
} from "./incorporationArticlesDomain";

export const IncorporationArticlesSection: FC<{ form: IncorporationForm }> = observer(
  ({ form }) => {
    const { t } = useTranslation();
    const model = form.articlesModel;
    const locked = form.isLocked;
    return (
      <VBox gap={"md"}>
        <b>{t(getDocumentLabel(DocumentType.articlesofassociation))}</b>
        <MRadioButtonGroup model={model.isCustomModel} horizontal disabled={locked} />
        {model.isCustom && (
          <MSelect
            model={model.documentLanguageModel}
            label={t("dbot.documentLanguage")}
            fullWidth
            disabled={locked}
          />
        )}
        <VBox gap={"lg"}>
          {form.autoArticles.map((article) => (
            <ArticleRow
              key={article.paragraph}
              article={article}
              dragHandle={
                model.isCustom && (
                  <DragHandle tooltip={{ key: "decisions.incorporation.auto-article-locked" }} />
                )
              }
            >
              {model.isCustom && <Box fg={"subtle"}>{t("common.auto")}</Box>}
            </ArticleRow>
          ))}
          {model.isCustom ? (
            <SortableList
              items={model.customSummaries}
              onMove={(article, toArticle) => model.moveArticle(article.id, toArticle.id)}
            >
              {model.customSummaries.map((article, index) => (
                <Sortable key={article.id} id={article.id} isDragDisabled={locked}>
                  {(dragProps) => (
                    <ArticleRow
                      article={article}
                      dragHandle={
                        <DragHandle dragProps={dragProps.isDragDisabled ? undefined : dragProps} />
                      }
                    >
                      <LinkButton
                        label={t("edit")}
                        icon={"edit"}
                        disabled={locked}
                        onClick={() => model.openEditDialog(index)}
                      />
                      <IconButton
                        icon={"trash"}
                        color={"error"}
                        tooltip={t("delete")}
                        disabled={locked}
                        onClick={() => model.removeArticle(index)}
                      />
                    </ArticleRow>
                  )}
                </Sortable>
              ))}
            </SortableList>
          ) : (
            model.summaries.map((article) => (
              <ArticleRow key={article.paragraph} article={article} />
            ))
          )}
        </VBox>
        {model.isCustom && (
          <>
            <Box>
              <AddArticleMenu model={model} disabled={locked} />
            </Box>
            {model.error && <Alert type={"error"} message={t(model.error)} />}
          </>
        )}
        {model.dialog && <IncorporationArticleDialog model={model.dialog} isLocked={locked} />}
      </VBox>
    );
  },
);

const AddArticleMenu: FC<{ model: IncorporationArticlesModel; disabled: boolean }> = observer(
  ({ model, disabled }) => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState("");
    const query = filter.trim().toLowerCase();
    return (
      <DropdownMenu
        disabled={disabled}
        button={{
          type: "link",
          label: t("articlesOfAssociation.addArticle"),
          icon: "plus",
          disabled,
        }}
        filter={filter}
        filterPlaceholder={{ key: "article.search-types" }}
        onFilterChange={setFilter}
        items={SELECTABLE_ARTICLE_TYPES.filter((info) =>
          t(info.name).toLowerCase().includes(query),
        ).map((info) => ({
          type: "item",
          label: info.name,
          isDisabled: model.isTypeTaken(info.type),
          onSelect: () => {
            setFilter("");
            model.openAddDialog(info.type);
          },
        }))}
      />
    );
  },
);

const LawLinks: FC<{ law: LawReference[] }> = ({ law }) => {
  const { t } = useTranslation();
  return (
    <Box textSize={"sm"} display={"block"}>
      <span className={"subtle"}>{t("article-type.companies-act")}</span>{" "}
      {law.map((reference, index) => (
        <Fragment key={reference.section}>
          {index > 0 && ", "}
          <a href={lawReferenceUrl(reference)} target={"_blank"} rel="noreferrer">
            {lawReferenceLabel(reference)}
          </a>
        </Fragment>
      ))}
    </Box>
  );
};

const DragHandle: FC<{ dragProps?: SortableChildrenProps; tooltip?: TMessage }> = ({
  dragProps,
  tooltip,
}) => (
  <Box
    fg={"subtle"}
    className={dragProps ? undefined : "opacity-50"}
    tooltip={tooltip}
    htmlProps={dragProps && { ...dragProps.dragAttributes, ...dragProps.dragListeners }}
  >
    <Icon name={"dots-six-vertical"} />
  </Box>
);

const ArticleRow: FC<{ article: ArticleSummary; dragHandle?: ReactNode; children?: ReactNode }> = ({
  article,
  dragHandle,
  children,
}) => (
  <Grid alignItems={"center"}>
    <GridItem xs={children ? 9 : 12}>
      <Box alignItems={"center"} gap={"sm"}>
        {dragHandle}
        <VBox gap={false} grow className={"min-w-0"}>
          <div className={"overflow-hidden text-ellipsis whitespace-nowrap"}>
            § {article.paragraph} {article.title}
          </div>
          <div className={"text-sm subtle overflow-hidden text-ellipsis whitespace-nowrap"}>
            {article.text}
          </div>
        </VBox>
      </Box>
    </GridItem>
    {children && (
      <GridItem xs={3}>
        <Box justifyContent={"end"} alignItems={"center"} gap={"lg"}>
          {children}
        </Box>
      </GridItem>
    )}
  </Grid>
);

const IncorporationArticleDialog: FC<{
  model: IncorporationArticleDialogModel;
  isLocked: boolean;
}> = observer(({ model, isLocked }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t(model.isEdit ? "article.edit" : "articlesOfAssociation.addArticle")}
      submitLabel={t("save")}
      onSubmit={model.submit}
      isSubmitDisabled={isLocked || !model.isValid}
      onCancel={model.onClose}
    >
      <VBox gap={"lg"}>
        <VBox gap={"xs"}>
          <Grid gap={"md"} alignItems={"end"}>
            <GridItem xs={3}>
              <MSelect
                model={model.paragraphModel}
                label={t("article.provision")}
                fullWidth
                disabled={isLocked}
              />
            </GridItem>
            <GridItem xs={9}>
              <MSelect
                model={model.typeModel}
                label={t("ownership.type")}
                fullWidth
                disabled={isLocked}
              />
            </GridItem>
          </Grid>
          <Box fg={"subtle"} textSize={"sm"} display={"block"}>
            {t(model.info.description)} <InfoButton infoText={model.info.tooltip} />
          </Box>
          {model.info.law.length > 0 && <LawLinks law={model.info.law} />}
          {model.typeError && <Alert type={"error"} message={t(model.typeError)} />}
        </VBox>
        <ArticleValueInput model={model} isLocked={isLocked} />
        {model.languages.map((language) => {
          const title = model.titles.find((field) => field.locale === language);
          const text = model.texts.find((field) => field.locale === language);
          return title && text ? (
            <VBox key={language} gap={"md"}>
              {model.languages.length > 1 && <h4>{t(languageLabel(language))}</h4>}
              <MTextInput
                model={title.model}
                label={t("decisions.freetext.title")}
                readOnly={isLocked}
              />
              {model.isRichText ? (
                <VBox gap={"xs"}>
                  <MarkdownEditor
                    label={t("article.provision")}
                    value={text.model.inputValue}
                    onChange={text.model.setAndCommitInputValue}
                    readOnly={isLocked || model.isTextFixed}
                  />
                  {text.model.error && (
                    <Box fg={"error"} textSize={"sm"}>
                      {t(text.model.error)}
                    </Box>
                  )}
                </VBox>
              ) : (
                <MTextArea
                  model={text.model}
                  label={t("article.provision")}
                  rows={4}
                  expandable
                  maxLength={model.info.maxLength}
                  readOnly={isLocked || model.isTextFixed}
                />
              )}
            </VBox>
          ) : null;
        })}
      </VBox>
    </Dialog>
  );
});

const ArticleValueInput: FC<{
  model: IncorporationArticleDialogModel;
  isLocked: boolean;
}> = observer(({ model, isLocked }) => {
  const { t } = useTranslation();
  switch (model.info.valueKind) {
    case "number":
      return (
        <MTextInput model={model.numberModel} label={t(model.info.name)} disabled={isLocked} />
      );
    case "yesNo":
      return (
        <VBox gap={"xs"}>
          {t(model.info.name)}
          <MRadioButtonGroup model={model.yesNoModel} horizontal disabled={isLocked} />
        </VBox>
      );
    case "municipality":
      return (
        <Autocomplete
          hideCaret
          label={t("article.municipality")}
          placeholder={t("search")}
          disabled={isLocked}
          items={model.suggestedMunicipalities}
          idFn={(item) => item.number}
          labelFn={(item) => item.name}
          captionFn={(item) => item.number}
          renderInputValue={(item) => item.name}
          value={model.municipality ?? null}
          inputValue={model.municipalitySearchModel.inputValue}
          onInputValueChange={model.setMunicipalitySearch}
          onSelect={model.selectMunicipality}
          onClear={model.clearMunicipality}
          loading={model.isSearchingMunicipalities}
          minQueryLength={1}
          error={model.municipalityError !== undefined}
          message={model.municipalityError && t(model.municipalityError)}
        />
      );
    case "ceoAppointment":
      return (
        <MSelect
          model={model.ceoAppointmentModel}
          label={t("article.ceo-appointed-by")}
          fullWidth
          disabled={isLocked}
        />
      );
    case "shareClass":
      return (
        <MSelect
          model={model.shareClassModel}
          label={t("cap-table.share-class")}
          fullWidth
          disabled={isLocked}
        />
      );
    case "signatureRight":
      return (
        <MSelect
          model={model.signatureRightModel}
          label={t(model.info.name)}
          fullWidth
          disabled={isLocked}
        />
      );
    case "none":
      return null;
  }
});
